"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ItemQuantity = void 0;
var _components = require("@ori-ui/components");
var _mui = require("@ori-ui/mui");
var _react = require("react");
var _context = require("../../context");
var _hooks = require("../../hooks");
var _logger = require("../../logger");
var _jsxRuntime = require("react/jsx-runtime");
const QuantityText = (0, _mui.styled)('div')(({
  theme
}) => ({
  fontSize: theme.typography.body2.fontSize,
  alignSelf: 'center'
}));
const ItemQuantity = () => {
  const {
    code,
    isAvailable,
    isBundleItem,
    itemTestId,
    lineNumber,
    maxQuantity,
    minQuantity,
    quantity,
    quantityPickerDisabled,
    quantityPickerDelayTimer,
    onQuantityChange
  } = (0, _context.useItemContext)();
  const {
    translations
  } = (0, _hooks.useTranslations)();
  const [value, setValue] = (0, _react.useState)(quantity);
  const [changeOrigin, setChangeOrigin] = (0, _react.useState)(undefined);
  const [ready, setReady] = (0, _react.useState)(false);
  const debounceTimer = (0, _react.useMemo)(() => quantityPickerDelayTimer ?? 300, [quantityPickerDelayTimer]);
  const pickerId = `${lineNumber}`;
  const testId = `${itemTestId}-quantity`;
  const minValue = (0, _react.useMemo)(() => typeof minQuantity === 'number' ? minQuantity < 0 ? 0 : minQuantity : 0, [minQuantity]);
  (0, _logger.logDebug)(`${lineNumber}-${code} -> ItemQuantity q:${quantity} value:${value}`);
  const handleChange = (0, _react.useCallback)((inputValue, id, source) => {
    setValue(inputValue);
    setChangeOrigin(source);

    // do not trigger onQuantityChange if the input value is not a number and equal or greater than 0
    if (typeof inputValue !== 'number') {
      return;
    }

    // check min and max (that also checks canReduce and canDelete by inheritance)
    let currentValue = inputValue;
    if (currentValue < minValue) {
      currentValue = minValue;
    }
    if (currentValue > maxQuantity) {
      currentValue = maxQuantity;
    }

    // if there was no allowed change in quantity just return
    if (currentValue === quantity) {
      setValue(currentValue);
      return;
    }

    // there was valid update so trigger onQuantityChange with a bit of delay
    setValue(currentValue);
    setReady(true);
  }, [maxQuantity, minValue, quantity]);
  const triggerOnQuantityChange = (0, _react.useCallback)(() => {
    if (changeOrigin && ready) {
      onQuantityChange?.(value, pickerId, changeOrigin);
      setReady(false);
    }
  }, [changeOrigin, onQuantityChange, pickerId, ready, value]);
  const handleClickAway = (0, _react.useCallback)(event => {
    setValue(prev => typeof prev === 'number' && prev >= 0 ? prev : quantity);
  }, [quantity]);
  (0, _react.useEffect)(() => {
    const timeoutId = setTimeout(() => {
      triggerOnQuantityChange();
    }, debounceTimer);
    return () => clearTimeout(timeoutId);
  }, [debounceTimer, ready, triggerOnQuantityChange]);
  (0, _react.useEffect)(() => {
    (0, _logger.logDebug)(`${lineNumber}-${code} -> quantity changed: ${quantity}`);
    setValue(quantity);
  }, [code, lineNumber, quantity]);
  if (!isAvailable || isBundleItem || quantityPickerDisabled) {
    return /*#__PURE__*/(0, _jsxRuntime.jsxs)(QuantityText, {
      "data-testid": testId,
      children: [translations.qty, ": ", quantity]
    });
  }
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.QuantityPicker, {
    id: pickerId,
    value: value,
    minValue: minValue,
    maxValue: maxQuantity,
    height: 3.6,
    DecreaseIconButtonProps: {
      'data-testid': `${testId}-decrease`,
      'aria-label': `- ${translations.quantity} ${code}`
    },
    IncreaseIconButtonProps: {
      'data-testid': `${testId}-increase`,
      'aria-label': `+ ${translations.quantity} ${code}`
    },
    InputProps: {
      inputProps: {
        'data-testid': `${testId}-input`,
        'aria-label': `${translations.quantity} ${code}`
      },
      type: 'tel'
    },
    onChange: handleChange,
    onClickAway: handleClickAway
  });
};
exports.ItemQuantity = ItemQuantity;
//# sourceMappingURL=ItemQuantity.js.map